/**
 * "Autonomy gates" (docs section 6): on the left the chain that a write to the world
 * outside I2Q must pass (the guard tests, the egress gate, the F&O writer, the seven F&O
 * gates), every gate closed. On the right the `autonomy_*` switch rows: the only
 * openings, each with its default and what it opens inside I2Q. Under them stand the
 * environment switches of the four steps after the ladder (13 Oct 2026: verifier, second
 * opinion, investigator, pre-flight), each off by default. On a phone the switch rows
 * stand under the chain (the narrow media query).
 */

import { Ban, Icon, Lock, LockOpen } from "@norican/icons";
import { Badge, Card, cn, Timeline, type TimelineItem, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

// ste-allow: product-term
export const AUTONOMY_GATES_TITLE = "Autonomy gates";

export const CHAIN_HEADING = "A write to the world outside I2Q must pass every gate";
/** A gate of the chain: its name, the file that holds it, and why nothing passes. */
export const WRITE_GATES: readonly (readonly [string, string, string])[] = [
  [
    "Guard tests",
    "backend/tests/",
    "`test_no_send_guard`, `test_fo_no_write` and `test_egress_gate` fail if the code can send mail or write to F&O.",
  ],
  [
    "Egress gate",
    "backend/src/rfq_foundry/egress.py",
    "Each role has an allow-list of hosts. The hard blocks for F&O, SMTP and Outlook win.",
  ],
  [
    "F&O writer",
    "backend/src/rfq_foundry/engine/pipeline/fo_writer.py",
    "A dry run by default. A live call needs `execute=True` and an approval token that nothing sets.",
  ],
  [
    "F&O gates G1 to G7",
    "backend/src/rfq_foundry/fo/gates.py",
    "Seven gates before a write to F&O. I2Q opens none of them.",
  ],
];
export const CHAIN_END = "The world outside I2Q";
export const CHAIN_END_TEXT = "I2Q sends no mail. I2Q writes nothing to F&O.";
export const CLOSED = "closed";

/** The seven gates of `fo/gates.py`, with the labels of that file. */
export const FO_GATES: readonly (readonly [string, string])[] = [
  ["G1", "Writer identity and grant"],
  ["G2", "Egress role and network rule"],
  ["G3", "Code gate in the writer"],
  ["G4", "Environment approval on a writer job"],
  ["G5", "Per-user role"],
  ["G6", "Owner approval per write"],
  ["G7", "Target endpoint"],
];

export const SWITCHES_HEADING = "The only openings: the switch rows `autonomy_*`";
export const SWITCHES_RULE =
  "A switch row opens one behaviour inside I2Q. No switch row opens a gate of the chain.";
/** A switch row: its name, its default in the DDL files, and what it opens. */
export const AUTONOMY_SWITCHES: readonly (readonly [string, boolean, string])[] = [
  ["autonomy_close_answered", true, "The queue closes an inquiry that got a reply outside I2Q."],
  ["autonomy_close_no_activity", true, "An inquiry closes after 21 days without activity."],
  ["autonomy_decide_exact_number", true, "An exact item number counts as decided by I2Q (UK)."],
  ["autonomy_suggest_owner", true, 'The queue shows the suggestion "Usually handled by".'],
  ["autonomy_reference_prices", false, "A line shows the list price of another entity."],
  ["autonomy_file_portal_notices", false, 'Portal and tender notices leave "To review".'],
  ["autonomy_link_thread_split", false, "A reply without a thread link joins its first mail."],
  [
    "autonomy_join_colleague_reply",
    false,
    "A colleague's internal reply is filed on its open case, with no lines.",
  ],
  ["autonomy_prefill_reply_draft", false, "I2Q prepares the reply for a ready inquiry."],
  [
    "autonomy_prefill_reply_draft_certain",
    false,
    "The same for an open inquiry with a certain customer and an item on each line.",
  ],
  ["autonomy_replied_from_fo", false, "A quotation in F&O files the inquiry as replied."],
  [
    "autonomy_close_desk_deleted",
    false,
    "The queue closes an inquiry that sales people deleted from the shared mailbox.",
  ],
];
export const SWITCHES_NOTE =
  "The badge is the default of the DDL files. A row that is missing reads as OFF. The live row in `mirror.sync_switch` decides.";

export const CHECKS_HEADING = "Environment switches of the four steps after the ladder";
export const CHECKS_RULE =
  "Each default is off. No switch here opens a gate of the chain, and no switch makes a customer certain.";
/** An environment switch of 13 Oct 2026: its name and what the step does when it is on. */
export const CHECK_SWITCHES: readonly (readonly [string, string])[] = [
  ["RFQ_SPARE_VERIFIER", "The verifier keeps or removes an automatic accept."],
  [
    "RFQ_SPARE_VERIFIER_PROMOTE",
    "The verifier can also accept a line. This is the one switch here that adds an automatic accept.",
  ],
  [
    "RFQ_CALIBRATION_MODE",
    "The second opinion records its verdict. In the mode `gate` it removes an automatic accept.",
  ],
  [
    "RFQ_MATCHING_INVESTIGATION_MODE",
    "The investigator proposes an account or an item. A sales person confirms it.",
  ],
  ["RFQ_PREFLIGHT", "Pre-flight flags the finished draft. It holds nothing."],
];
// Round 2 of the investigator (13 Oct 2026) adds 17 names, each off by default: a mode for
// each entity, the playbooks, the second pass, the on-demand search. The figure names none
// of them; docs section 6 lists every one.
export const CHECKS_NOTE =
  "The value is `off`, `on` or a list of entities, and a mode for two of them. Round 2 of the investigator adds more switches, each off by default. Docs section 6 has every name and its default.";

const COLUMN = "flex min-w-0 flex-col gap-3";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

/** A lock with a word: open and green for ON, closed and grey for every other state. */
function StateBadge({ open = false, label }: { open?: boolean; label: string }) {
  return (
    <Badge tone={open ? "success" : "neutral"} variant={open ? "subtle" : "outline"} size="sm">
      <Icon icon={open ? LockOpen : Lock} size="xs" />
      {label}
    </Badge>
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function AutonomyGatesFigure(props: { caption?: string; layout?: "beside" | "stacked" }) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "beside");
  const chain: TimelineItem[] = WRITE_GATES.map(([title, path, text], index) => ({
    id: title,
    title: (
      <span className="flex flex-wrap items-center gap-1.5">
        {title}
        <StateBadge label={CLOSED} />
      </span>
    ),
    icon: <Icon icon={Lock} size="sm" />,
    description: (
      <span className="flex min-w-0 flex-col items-start gap-1">
        <span className="wrap-anywhere">{code(text)}</span>
        <Typography as="code" variant="code" tone="muted" className="text-[0.8em] break-all">
          {path}
        </Typography>
        {index === WRITE_GATES.length - 1 && (
          <span className="flex flex-wrap gap-1">
            {FO_GATES.map(([gate, label]) => (
              <StateBadge key={gate} label={`${gate} ${label}`} />
            ))}
          </span>
        )}
      </span>
    ),
  }));
  chain.push({
    id: "world",
    title: CHAIN_END,
    tone: "danger",
    icon: <Icon icon={Ban} size="sm" />,
    description: CHAIN_END_TEXT,
  });
  return (
    <Figure name="autonomy-gates" title={AUTONOMY_GATES_TITLE} caption={props.caption}>
      <div
        data-layout={form}
        className={
          form === "stacked" ? COLUMN : "grid grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-4"
        }
      >
        <div className={COLUMN}>
          <Typography variant="label">{CHAIN_HEADING}</Typography>
          <Timeline
            orientation="vertical"
            connector="arrow"
            items={chain}
            aria-label={CHAIN_HEADING}
          />
        </div>
        <div className={COLUMN}>
          <Card
            variant="outline"
            padding="sm"
            accent="left"
            accentTone="success"
            className={COLUMN}
          >
            <Typography variant="label">{code(SWITCHES_HEADING)}</Typography>
            <Typography variant="body-sm" className="break-words">
              {SWITCHES_RULE}
            </Typography>
            <ul className="flex flex-col gap-2">
              {AUTONOMY_SWITCHES.map(([name, on, opens]) => (
                <li key={name} className="flex min-w-0 flex-col gap-0.5 text-sm">
                  <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <StateBadge open={on} label={on ? "ON" : "OFF"} />
                    <Typography as="code" variant="code" className="text-[0.85em] break-all">
                      {name}
                    </Typography>
                  </span>
                  <span className={cn("min-w-0 break-words", !on && "text-fg-muted")}>{opens}</span>
                </li>
              ))}
            </ul>
            <Typography variant="caption" tone="muted" className="break-words">
              {code(SWITCHES_NOTE)}
            </Typography>
          </Card>
          <Card
            variant="outline"
            padding="sm"
            role="group"
            aria-label={CHECKS_HEADING}
            data-check-switches
            className={COLUMN}
          >
            <Typography variant="label">{CHECKS_HEADING}</Typography>
            <Typography variant="body-sm" className="break-words">
              {CHECKS_RULE}
            </Typography>
            <ul className="flex flex-col gap-2">
              {CHECK_SWITCHES.map(([name, does]) => (
                <li key={name} className="flex min-w-0 flex-col gap-0.5 text-sm">
                  <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <StateBadge label="off" />
                    <Typography as="code" variant="code" className="text-[0.85em] break-all">
                      {name}
                    </Typography>
                  </span>
                  <span className="text-fg-muted min-w-0 break-words">{code(does)}</span>
                </li>
              ))}
            </ul>
            <Typography variant="caption" tone="muted" className="break-words">
              {code(CHECKS_NOTE)}
            </Typography>
          </Card>
        </div>
      </div>
    </Figure>
  );
}
